import React, { useMemo } from 'react';
import { useProgressBar } from '@react-aria/progress';
import * as variables from '../../styles/colors.module.scss';
import { Stack } from '../Stack/Stack';

export enum SpinnerSize {
    big = 'big',
    small = 'small',
}

export enum SpinnerVariant {
    primary = 'primary',
    secondary = 'secondary',
}

export interface ISpinnerProps {
    'aria-label'?: string;
    label?: string;
    size?: SpinnerSize;
    variant?: SpinnerVariant;
    className?: string;
}

const labelStyles = {
    marginTop: '16px',
};

export const Spinner: React.FunctionComponent<ISpinnerProps> = props => {
    const { progressBarProps, labelProps } = useProgressBar({
        isIndeterminate: true,
        'aria-label': props['aria-label'],
        label: props.label,
    });

    const { variant = SpinnerVariant.secondary, size: spinnerSize = SpinnerSize.small } = props;

    const [ size, width, center, r, c, offset ] = useMemo(() => {
        const size = spinnerSize === SpinnerSize.big ? 64 : 32;
        const width = spinnerSize === SpinnerSize.big ? 8 : 4;

        const center = Math.round(size / 2);
        const r = center - width;
        const c = 2 * r * Math.PI;
        const offset = c - (1 / 4) * c;

        return [ size, width, center, r, c, offset ];
    }, [ spinnerSize ]);

    const [ backgroundColor, foregroundColor ] = useMemo(() => {
        if (variant === SpinnerVariant.primary) {
            return [ variables.colorGrayLight, variables.colorPrimary ];
        }
        return [ variables.colorGrayLight, variables.colorGrayDark ];
    }, [ variant ]);

    return (
        <Stack align="center" justify="center" className={props.className}>
            <svg
                {...progressBarProps as React.SVGProps<SVGSVGElement>}
                width={size}
                height={size}
                viewBox={`0 0 ${ size } ${ size }`}
                fill="none"
                strokeWidth={width}
            >
                <circle
                    role="presentation"
                    cx={center}
                    cy={center}
                    r={r}
                    stroke={backgroundColor}
                />
                <circle
                    role="presentation"
                    cx={center}
                    cy={center}
                    r={r}
                    stroke={foregroundColor}
                    strokeDasharray={c}
                    strokeDashoffset={offset}
                >
                    <animateTransform
                        attributeName="transform"
                        type="rotate"
                        begin="0s"
                        dur="1s"
                        from={`0 ${ center } ${ center }`}
                        to={`360 ${ center } ${ center }`}
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
            {!!props.label && (
                <span {...labelProps} style={labelStyles}>{props.label}</span>
            )}
        </Stack>
    );
};
